import type { Bar, Pivot, RegimeResult, Timeframe } from '../types';

export interface AnalysisConfig { pivotStrength: number; minPivotTicks: number; tickSize: number; rsiPeriod: number }
export const defaultConfig: AnalysisConfig = { pivotStrength: 3, minPivotTicks: 8, tickSize: 0.25, rsiPeriod: 14 };

export function findConfirmedPivots(bars: Bar[], config = defaultConfig): Pivot[] {
  const { pivotStrength: s, minPivotTicks, tickSize } = config;
  const pivots: Pivot[] = [];
  for (let i = s; i < bars.length - s; i++) {
    const window = bars.slice(i - s, i + s + 1);
    const isHigh = window.every((b, j) => j === s || bars[i].high > b.high);
    const isLow = window.every((b, j) => j === s || bars[i].low < b.low);
    const candidate = isHigh ? { kind: 'high' as const, price: bars[i].high } : isLow ? { kind: 'low' as const, price: bars[i].low } : null;
    if (!candidate) continue;
    const previous = [...pivots].reverse().find(p => p.kind === candidate.kind);
    if (previous && Math.abs(candidate.price - previous.price) < minPivotTicks * tickSize) continue;
    pivots.push({ index: i, time: bars[i].time, ...candidate, confirmedAt: bars[i + s].time });
  }
  const byKind: Record<'high' | 'low', Pivot | undefined> = { high: undefined, low: undefined };
  return pivots.map(p => {
    const prev = byKind[p.kind];
    p.label = prev ? (p.kind === 'high' ? (p.price > prev.price ? 'HH' : 'LH') : (p.price > prev.price ? 'HL' : 'LL')) : undefined;
    byKind[p.kind] = p;
    return p;
  });
}

export function calculateRsi(bars: Bar[], period = 14): Array<number | null> {
  const out: Array<number | null> = Array(bars.length).fill(null);
  if (bars.length <= period) return out;
  let gains = 0, losses = 0;
  for (let i = 1; i <= period; i++) { const d = bars[i].close - bars[i - 1].close; gains += Math.max(d, 0); losses += Math.max(-d, 0); }
  let avgGain = gains / period, avgLoss = losses / period;
  out[period] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  for (let i = period + 1; i < bars.length; i++) {
    const d = bars[i].close - bars[i - 1].close;
    avgGain = (avgGain * (period - 1) + Math.max(d, 0)) / period;
    avgLoss = (avgLoss * (period - 1) + Math.max(-d, 0)) / period;
    out[i] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  }
  return out;
}

export function detectDivergence(bars: Bar[], pivots: Pivot[], period = 14): Array<{ kind: 'bullish' | 'bearish'; first: Pivot; second: Pivot }> {
  const rsi = calculateRsi(bars, period); const found: Array<{ kind: 'bullish' | 'bearish'; first: Pivot; second: Pivot }> = [];
  for (const kind of ['low', 'high'] as const) {
    const p = pivots.filter(x => x.kind === kind);
    for (let i = 1; i < p.length; i++) {
      const a = p[i - 1], b = p[i], ra = rsi[a.index], rb = rsi[b.index];
      if (ra == null || rb == null) continue;
      if (kind === 'low' && b.price < a.price && rb > ra) found.push({ kind: 'bullish', first: a, second: b });
      if (kind === 'high' && b.price > a.price && rb < ra) found.push({ kind: 'bearish', first: a, second: b });
    }
  }
  return found;
}

export function classifyRegime(bars: Bar[], timeframe: Timeframe, config = defaultConfig): RegimeResult {
  const pivots = findConfirmedPivots(bars, config); const recent = pivots.slice(-6);
  const highs = recent.filter(p => p.kind === 'high').slice(-2); const lows = recent.filter(p => p.kind === 'low').slice(-2);
  const reasons: string[] = [];
  if (highs.length < 2 || lows.length < 2) return { timeframe, regime: 'transition', reasons: ['Insufficient confirmed swing sequence'], confirmed: false };
  const highUp = highs[1].price > highs[0].price, lowUp = lows[1].price > lows[0].price;
  if (highUp && lowUp) { reasons.push('Confirmed higher high and higher low'); return { timeframe, regime: 'uptrend', reasons, confirmed: true }; }
  if (!highUp && !lowUp) { reasons.push('Confirmed lower high and lower low'); return { timeframe, regime: 'downtrend', reasons, confirmed: true }; }
  const ranges = bars.slice(-20).map(b => b.high - b.low); const avgRange = ranges.reduce((a, b) => a + b, 0) / Math.max(ranges.length, 1);
  const net = Math.abs(bars.at(-1)!.close - bars[Math.max(0, bars.length - 20)].open);
  if (net < avgRange * 1.5) return { timeframe, regime: 'range', reasons: ['Net movement is small relative to recent bar range', 'Swing structure is mixed'], confirmed: true };
  return { timeframe, regime: lowUp ? 'bullish-reversal' : 'bearish-reversal', reasons: [lowUp ? 'Higher low with unresolved high structure' : 'Lower high with unresolved low structure'], confirmed: false };
}

export function sessionVwap(bars: Bar[]) {
  let pv = 0, volume = 0, p2v = 0;
  return bars.map(b => { const typical = (b.high + b.low + b.close) / 3; pv += typical * b.volume; p2v += typical * typical * b.volume; volume += b.volume; const vwap = pv / volume; const sd = Math.sqrt(Math.max(0, p2v / volume - vwap * vwap)); return { time: b.time, vwap, upper: vwap + sd, lower: vwap - sd }; });
}
